<template>
  <Header
    @switchVersion="switchVersion"
    :disableRestoreVersionBtn="disableRestoreVersionBtn"
    :sender-preferences="senderPreferences"
    @contact:click="showContactPreview = true"
    @update-sender-preference="(val:any)=>senderPreferences = val"
  />
  <div v-if="isLoading" class="flex w-full h-full justify-center items-center">
    <UISpin />
  </div>
  <Builder
    :class="{
      hidden: currentTabComponent !== routeNames.EMAIL_TEMPLATE_BUILDER,
    }"
    v-if="loadBuilder && !isLoading"
    :key="userId + reloadBuilder"
  ></Builder>
  <div
    class="overflow-y-auto preview-container"
    v-if="currentTabComponent === routeNames.EMAIL_PREVIEW_TEMPLATE"
  >
    <DevicePreview :previewHtml="previewHtml" />
  </div>
  <div
    class="h-full"
    v-if="
      currentTabComponent === routeNames.EMAIL_TEMPLATE_VERSIONS && templateId
    "
  >
    <Versions
      ref="templateVersionsRef"
      :templateId="templateId"
      :userId="userId"
      :locationId="locationId"
      :service="service"
      @update:version="item => (disableRestoreVersionBtn = item.id === 0)"
    />
  </div>
  <ContactPanel
    :service="service"
    :state="store.state"
    :location-id="(appState.locationId as string)"
    :show="showContactPreview"
    @update:select="val => (selectedContact = val)"
    @update:close="showContactPreview = false"
    :to="appState.isModalDialog ? '#emailPreview' : 'body'"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, inject, ref, watch } from 'vue'
import Header from '../components/Header.vue'
import Builder from '../pages/Builder.vue'
import { useStore } from '../store'
import { useService } from '../service'
import { ISchedule } from '../types'
import { UISpin } from '@gohighlevel/ghl-ui'
import DevicePreview from 'common/components/Preview/DevicePreview.vue'
import { builderState } from 'common/composable/manageBuilderState'
import Versions from 'common/components/TemplateVersions/Versions.vue'
import { EMAIL_PREVIEW_ROUTES_NAMES } from 'common/utils/const_helper'
import { getHandleBarCustomValues } from 'common/utils/helper'
import ContactPanel from 'common/components/ContactPanel.vue'
import { appState } from '../../../common/composable/appState'
import { isEmpty } from 'lodash'
const route = inject<any>('route')
const store = useStore()
//  custom routing and loading components
const currentTabComponent = ref(appState.selectedPage)
const isLoading = ref(false)
const scheduleDetails = ref({})
const loadBuilder = computed(() => store.state.userId)
const scheduleId = appState.scheduleId
const service = useService()
const templateVersionsRef = ref()
const reloadBuilder = ref(false)
const disableRestoreVersionBtn = ref(true)

// contact variables
const showContactPreview = ref(false)
const selectedContact = ref(null)

if (scheduleId) {
  isLoading.value = true

  try {
    scheduleDetails.value = service
      .getEmailSchedule(String(scheduleId))
      .then(data => {
        // isLoading.value = false
        const schedule: ISchedule = data.data
        scheduleDetails.value = schedule
      })
      .catch(() => (isLoading.value = false))
  } catch (err) {
    console.warn(err)
  }
}

async function switchVersion() {
  if (templateVersionsRef.value?.switchVersions) {
    try {
      builderState.loader = true
      await templateVersionsRef.value.switchVersions()
      appState.selectedPage = EMAIL_PREVIEW_ROUTES_NAMES.EMAIL_TEMPLATE_BUILDER
      reloadBuilder.value = !reloadBuilder.value
    } catch (err) {
      console.error(err)
    } finally {
      builderState.loader = false
    }
  }
}
const previewHtml = ref()
const routeNames = EMAIL_PREVIEW_ROUTES_NAMES
const previewUrl = computed(() => builderState.previewUrl)
const templateId = computed(() => appState.templateId || '')
const tokenId = computed(() => store?.state?.tokenId || undefined)
const userId = computed(() => store?.state?.userId || undefined)
const locationId = computed(() => appState.locationId || undefined)
const senderPreferences = ref({})

const fetchEmailSettings = async () => {
  try {
    // avoid multiple calls
    if (!locationId.value || !isEmpty(senderPreferences.value)) return
    const { data } = await service.getSettings(locationId.value)
    if (data?.senderPreferences)
      senderPreferences.value = data?.senderPreferences
  } catch (error) {
    console.log(`Error while fetching email settings, error: ${error}`)
  }
}

watch(
  //first fetch should be call when token is available
  () => store?.state?.tokenId,
  (newVal, oldVal) => {
    if (newVal && !oldVal) {
      fetchEmailSettings()
    }
  }
)
onMounted(() => {
  if (store?.state?.tokenId) {
    fetchEmailSettings()
  }
})

watch(
  () => appState.selectedPage,
  (newVal, oldVal) => {
    if (newVal) currentTabComponent.value = newVal as string
  }
)
const updateCustomValues = async () => {
  if (previewUrl.value) {
    previewHtml.value = await getHandleBarCustomValues(
      previewUrl.value,
      store.state,
      selectedContact.value
    )
  }
}

watch(
  () => [
    previewUrl.value,
    tokenId.value,
    selectedContact.value,
    store.state.customValuesLocation,
  ],
  updateCustomValues,
  {
    immediate: true,
  }
)
</script>

<style>
.fonts-fallback .n-config-provider {
  height: 100%;
}
.preview-container {
  height: calc(100% - 56px);
}
</style>
